import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  BuildingOfficeIcon,
  UserIcon,
  CurrencyDollarIcon,
  ChartBarIcon,
} from '@heroicons/react/24/outline';
import agencyService from '../../services/agencyService';
import { useToast } from '../../contexts/ToastContext';

const AgencyList = () => {
  const [agencies, setAgencies] = useState([]);
  const [filteredAgencies, setFilteredAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;
  
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();

  useEffect(() => {
    fetchAgencies();
  }, [currentPage]);

  useEffect(() => {
    filterAgencies();
  }, [searchTerm, agencies]);

  const fetchAgencies = async () => {
    try {
      setLoading(true);
      const data = await agencyService.getAllAgencies({
        page: currentPage,
        limit: itemsPerPage,
      });
      setAgencies(data);
    } catch (error) {
      showError('Erreur lors du chargement des agences');
    } finally {
      setLoading(false);
    }
  };

  const filterAgencies = () => {
    let filtered = agencies;

    if (searchTerm) {
      filtered = filtered.filter(agency =>
        agency.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        agency.address?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredAgencies(filtered);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette agence ?')) {
      try {
        await agencyService.deleteAgency(id);
        showSuccess('Agence supprimée avec succès');
        fetchAgencies();
      } catch (error) {
        showError('Erreur lors de la suppression de l\'agence');
      }
    }
  };

  const AgencyCard = ({ agency }) => {
    return (
      <div className="card hover:shadow-lg transition-shadow duration-300">
        <div className="p-6">
          <div className="flex justify-between items-start">
            <div className="flex items-center">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">{agency.name}</h3>
                <p className="text-sm text-gray-500">{agency.code}</p>
              </div>
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => navigate(`/agencies/${agency.id}`)}
                className="text-blue-600 hover:text-blue-900"
                title="Voir détails"
              >
                <EyeIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => navigate(`/agencies/edit/${agency.id}`)}
                className="text-yellow-600 hover:text-yellow-900"
                title="Modifier"
              >
                <PencilIcon className="h-5 w-5" />
              </button>
              <button
                onClick={() => handleDelete(agency.id)}
                className="text-red-600 hover:text-red-900"
                title="Supprimer"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="flex items-center">
              <UserIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Clients: </span>
              <span className="text-sm font-medium text-gray-900 ml-1">
                {agency.total_clients || 0}
              </span>
            </div>
            <div className="flex items-center">
              <CurrencyDollarIcon className="h-4 w-4 text-gray-400 mr-2" />
              <span className="text-sm text-gray-600">Prêts: </span>
              <span className="text-sm font-medium text-gray-900 ml-1">
                {agency.active_loans || 0}
              </span>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex justify-between text-sm">
              <span className="text-gray-500">{agency.address || 'Adresse non renseignée'}</span>
              {agency.manager_name && (
                <span className="text-gray-700">Manager: {agency.manager_name}</span>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading && agencies.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <svg className="animate-spin h-8 w-8 text-primary-600 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <p className="mt-2 text-sm text-gray-600">Chargement des agences...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Agences</h1>
          <p className="mt-1 text-sm text-gray-600">
            Gestion des agences de la microfinance
          </p>
        </div>
        <button
          onClick={() => navigate('/agencies/new')}
          className="btn-primary"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Nouvelle Agence
        </button>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-4">
        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-blue-100 p-3">
                <BuildingOfficeIcon className="h-6 w-6 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Agences</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {agencies.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-green-100 p-3">
                <UserIcon className="h-6 w-6 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Total Clients</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {agencies.reduce((sum, agency) => sum + (agency.total_clients || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-yellow-100 p-3">
                <CurrencyDollarIcon className="h-6 w-6 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Prêts Actifs</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {agencies.reduce((sum, agency) => sum + (agency.active_loans || 0), 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        <div className="card">
          <div className="p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0 rounded-md bg-purple-100 p-3">
                <ChartBarIcon className="h-6 w-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600">Performance</p>
                <p className="text-2xl font-semibold text-gray-900">
                  92.5%
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="p-6">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="search" className="block text-sm font-medium text-gray-700">
                Rechercher
              </label>
              <input
                type="text"
                id="search"
                className="input-field mt-1"
                placeholder="Nom, code, adresse..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="sort" className="block text-sm font-medium text-gray-700">
                Trier par
              </label>
              <select id="sort" className="input-field mt-1">
                <option value="name">Nom (A-Z)</option>
                <option value="clients">Clients (décroissant)</option>
                <option value="recent">Plus récent</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Agencies Grid */}
      {filteredAgencies.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
          {filteredAgencies.map((agency) => (
            <AgencyCard key={agency.id} agency={agency} />
          ))}
        </div>
      ) : (
        <div className="card">
          <div className="p-12 text-center">
            <BuildingOfficeIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune agence trouvée</h3>
            <p className="mt-1 text-sm text-gray-500">
              {searchTerm
                ? 'Essayez de modifier vos critères de recherche.'
                : 'Commencez par créer votre première agence.'}
            </p>
            {!searchTerm && (
              <div className="mt-6">
                <button
                  onClick={() => navigate('/agencies/new')}
                  className="btn-primary"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Créer une agence
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AgencyList;